import { useEffect, useState } from "react";
import QRCode from "qrcode";
import { Loader2, ShieldCheck, Mail, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { errText } from "@/lib/aadhaar";

export const OtpBoxes = ({ value, onChange, testid }) => (
  <InputOTP maxLength={6} value={value} onChange={onChange} inputMode="numeric" pattern="^[0-9]*$" data-testid={testid} autoFocus>
    <InputOTPGroup>{[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="h-12 w-11 text-lg font-bold bg-[#F0F4F8] border-slate-300" />)}</InputOTPGroup>
  </InputOTP>
);

export const AuthenticatorQr = ({ url, secret }) => {
  const { L } = useLang();
  const [qr, setQr] = useState("");
  useEffect(() => { QRCode.toDataURL(url, { margin: 1, width: 200 }).then(setQr); }, [url]);
  return (
    <div className="rounded-2xl bg-[#F0F4F8] border border-slate-200 p-4 flex gap-4 items-center" data-testid="totp-qr-box">
      {qr && <img src={qr} alt="Authenticator QR" className="w-32 h-32 rounded-lg bg-white p-1" data-testid="totp-qr-img" />}
      <div className="text-xs text-slate-600 space-y-1 min-w-0">
        <p>{L("Scan with Google Authenticator (or any authenticator app), then type the 6-digit code.", "Google Authenticator (या कोई भी authenticator ऐप) से स्कैन करें, फिर 6 अंकों का कोड लिखें।")}</p>
        <p className="text-slate-500">{L("Or enter this key:", "या यह key डालें:")}</p>
        <code className="block break-all text-[#007A8A] font-mono text-[11px]" data-testid="totp-secret">{secret}</code>
      </div>
    </div>
  );
};

export const TwoFactorStep = ({ mfa, onSuccess }) => {
  const { L } = useLang();
  const [method, setMethod] = useState(mfa.method);
  const [sent, setSent] = useState(mfa.email_sent);
  const [enroll, setEnroll] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");

  const call = async (fn) => {
    setBusy(true); setErr(""); setInfo("");
    try { await fn(); } catch (e) { setErr(errText(e.response?.data?.detail)); setCode(""); } finally { setBusy(false); }
  };
  const verify = (e) => { e.preventDefault(); call(async () => onSuccess((await api.post("/auth/2fa/verify", { challenge: mfa.challenge, code, method })).data)); };
  const sendEmail = () => call(async () => {
    const { data } = await api.post("/auth/2fa/resend", { challenge: mfa.challenge });
    setMethod("email"); setEnroll(null); setCode(""); setSent(data.email_sent);
    setInfo(data.email_sent ? L("A new code is on its way.", "नया कोड भेज दिया गया है।") : "");
  });
  const startEnroll = () => call(async () => { setEnroll((await api.post("/auth/2fa/enroll", { challenge: mfa.challenge })).data); setMethod("totp"); setCode(""); });

  const isTotp = method === "totp";
  return (
    <form onSubmit={verify} className="space-y-4" data-testid="twofa-step">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-[#F0F4F8] grid place-items-center">{isTotp ? <Smartphone className="w-5 h-5 text-[#007A8A]" /> : <Mail className="w-5 h-5 text-[#007A8A]" />}</div>
        <div>
          <h2 className="text-xl font-extrabold text-[#0A192F]" data-testid="twofa-title">{L("Two-step verification", "दो-चरण सत्यापन")}</h2>
          <p className="text-xs text-slate-500" data-testid="twofa-hint">
            {isTotp ? L("Enter the 6-digit code from your authenticator app.", "अपने authenticator ऐप का 6 अंकों का कोड डालें।")
              : sent ? L(`Enter the 6-digit code sent to ${mfa.email_hint}.`, `${mfa.email_hint} पर भेजा गया 6 अंकों का कोड डालें।`)
              : L(`Email code for ${mfa.email_hint}`, `${mfa.email_hint} के लिए ईमेल कोड`)}
          </p>
        </div>
      </div>
      {enroll && <AuthenticatorQr url={enroll.otpauth_url} secret={enroll.secret} />}
      {!isTotp && !sent && (
        <p className="text-xs rounded-xl bg-amber-500/10 border border-amber-400/50 text-amber-700 p-3" data-testid="twofa-email-warning">
          {L("We couldn't send the email code right now.", "अभी ईमेल कोड नहीं भेज पाए।")} {mfa.can_enroll_totp ? L("Set up an authenticator app below instead.", "नीचे authenticator ऐप सेट करें।") : L("Try 'Resend code' in a minute.", "एक मिनट बाद 'कोड दोबारा भेजें' दबाएँ।")}
        </p>
      )}
      <OtpBoxes value={code} onChange={setCode} testid="twofa-code-input" />
      {err && <p className="text-sm text-red-600" data-testid="twofa-error">{err}</p>}
      {info && <p className="text-sm text-emerald-700" data-testid="twofa-info">{info}</p>}
      <Button type="submit" disabled={busy || code.length !== 6} className="w-full rs-cta h-11 rounded-full" data-testid="twofa-verify-btn">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ShieldCheck className="w-4 h-4 mr-1" />{L("Verify & continue", "सत्यापित करें")}</>}
      </Button>
      <div className="flex flex-wrap gap-x-4 gap-y-2 text-xs">
        <button type="button" onClick={sendEmail} disabled={busy} className="text-[#007A8A] hover:underline" data-testid="twofa-email-btn">
          {isTotp ? L("Send a code to my email instead", "इसके बजाय ईमेल पर कोड भेजें") : L("Resend code", "कोड दोबारा भेजें")}
        </button>
        {!isTotp && mfa.methods.includes("totp") && <button type="button" onClick={() => { setMethod("totp"); setCode(""); }} className="text-[#007A8A] hover:underline" data-testid="twofa-use-totp-btn">{L("Use authenticator app", "Authenticator ऐप इस्तेमाल करें")}</button>}
        {mfa.can_enroll_totp && !enroll && <button type="button" onClick={startEnroll} disabled={busy} className="text-[#007A8A] hover:underline" data-testid="twofa-enroll-btn">{L("Set up an authenticator app", "Authenticator ऐप सेट करें")}</button>}
      </div>
    </form>
  );
};
